import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAddToCartMutation } from "../../cart/api/cartApi";
import { useRemoveSavedForLaterItemMutation } from "../api/savedForLaterApi";

/**
 * Moves an item from Saved For Later back to Cart via frontend orchestration.
 */
export const useMoveSavedForLaterItemBackToCart = () => {
  const { t } = useTranslation();
  const [addToCart] = useAddToCartMutation();
  const [removeSavedItem] = useRemoveSavedForLaterItemMutation();

  const [isMoving, setIsMoving] = useState(false);

  const handleMoveSavedForLaterItemBackToCart = useCallback(
    async (id: number) => {
      setIsMoving(true);
      const toastId = toast.loading(
        t("cartItemActions.moving", "Moving to cart...")
      );

      try {
        // Step 1: Add to Cart
        // Note: For variations, passing just the ID to Store API's add-item usually works
        // if it's the variation ID.
        await addToCart({ id, quantity: 1 }).unwrap();

        // Step 2: Remove from Saved List
        await removeSavedItem(id).unwrap();

        toast.success(
          t("feedback.cart.product.addToCartSuccess", "Added back to cart!"),
          { id: toastId }
        );
      } catch (error) {
        console.error("Move to cart failed", error);
        toast.error(
          t(
            "feedback.cart.product.addToCartError",
            "Failed to move item to cart."
          ),
          { id: toastId }
        );
      } finally {
        setIsMoving(false);
      }
    },
    [addToCart, removeSavedItem, t]
  );

  return { handleMoveSavedForLaterItemBackToCart, isMoving };
};
